import LegalLayout from '../../components/LegalLayout';
import LegalPageChrome from '../../components/LegalPageChrome';
import MobileBackButton from '../../components/MobileBackButton';
import { LanguageProvider } from '../../context/LanguageContext';


export async function getServerSideProps(context) {
  const INTERNAL_API = process.env.INTERNAL_API_URL || 'http://backend:8000'
  let suggestedLanguage = 'en'
  try {
    const cfCountry = context.req.headers['cf-ipcountry'] || ''
    const res = await fetch(`${INTERNAL_API}/v1/geo/`, {
      headers: { 'CF-IPCountry': cfCountry },
    })
    if (res.ok) {
      const data = await res.json()
      if (['en', 'ht', 'fr', 'es', 'pt-BR', 'zh-Hans'].includes(data.suggested_language)) {
        suggestedLanguage = data.suggested_language
      }
    }
  } catch (_) { /* geo hint is best-effort */ }
  return { props: { suggestedLanguage } }
}


export default function TermsOfService({ suggestedLanguage }) {
  return (
    <LanguageProvider initialSuggestion={suggestedLanguage}>
    <LegalLayout>
      <div className="max-w-3xl mx-auto px-4 py-8 relative">
        <div className="bg-white dark:bg-dark-card shadow-md rounded-lg p-6">
          <LegalPageChrome pageKey="terms" backButton={<MobileBackButton />}>
          <div className="prose dark:prose-invert max-w-none">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing and using the services provided by CreoleCentric ("CreoleCentric," "we," "our," or "us") —
              a service operated by iTech Home Solutions, Inc., a Florida corporation —
              including our website at creolecentric.com and any related applications or services, you ("User," "you," or "your")
              agree to comply with and be bound by these Terms of Service ("Terms"). If you do not agree to these Terms,
              you must immediately discontinue use of our services.
            </p>
            <p>
              These Terms constitute a legally binding agreement between you and CreoleCentric. By clicking "I Agree," 
              creating an account, or using our services in any manner, you acknowledge that you have read, understood, 
              and agree to be bound by these Terms and our Privacy Policy.
            </p>
            
            <h2>2. Description of Service</h2>
            <p>
              CreoleCentric provides advanced artificial intelligence-powered text-to-speech (TTS) synthesis services 
              specifically designed for Haitian Creole, including:
            </p>
            <ul>
              <li>Text-to-speech conversion with multiple voice options and regional accents</li>
              <li>Multi-language translation to Haitian Creole from 50+ languages</li>
              <li>Real-time text highlighting synchronized with audio playback</li>
              <li>API access for developers and third-party integrations</li>
              <li>Custom Voices (Enterprise plans only)</li>
              <li>Batch processing for large-scale document conversion</li>
              <li>SSML (Speech Synthesis Markup Language) support</li>
              <li>Custom pronunciation dictionaries</li>
              <li>AI speech detection and classification tools</li>
            </ul>
            <p>
              Our services are designed to support education, cultural preservation, accessibility, and commercial applications 
              for the Creole-speaking community worldwide.
            </p>
            
            <h2>3. User Accounts and Registration</h2>
            <h3>3.1 Account Creation</h3>
            <p>
              To access certain features of our services, you must create an account by providing accurate, current, 
              and complete information. You must be at least 18 years old or have parental consent to create an account.
            </p>
            
            <h3>3.2 Account Security</h3>
            <p>
              You are responsible for:
            </p>
            <ul>
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Immediately notifying us of any unauthorized use or security breach</li>
              <li>Ensuring your account information remains accurate and up-to-date</li>
            </ul>
            
            <h3>3.3 Account Suspension or Termination</h3>
            <p>
              We reserve the right to suspend or terminate accounts that violate these Terms, engage in fraudulent activity, 
              or misuse our services. Terminated accounts may lose access to credits, generated content, and account data.
            </p>
            
            <h2>4. Subscription Plans and Credits</h2>
            <h3>4.1 Subscription Tiers</h3>
            <p>
              We offer various subscription plans:
            </p>
            <ul>
              <li><strong>Free Plan:</strong> 3,000 credits per month, basic features</li>
              <li><strong>Learner Plan:</strong> 30,000 credits per month</li>
              <li><strong>Creator Plan:</strong> 100,000 credits per month with rollover</li>
              <li><strong>Pro Plan:</strong> 500,000 credits per month with rollover</li>
              <li><strong>Business Plan:</strong> 2,000,000 credits per month with rollover</li>
              <li><strong>Enterprise Plan:</strong> 11,000,000 credits per month with rollover</li>
            </ul>
            
            <h3>4.2 Credit System</h3>
            <p>
              Credits are calculated based on character count (1 credit = 1 character, including spaces and punctuation). 
              Credits are consumed when generating audio content. Subscription credits refresh monthly on your billing date.
            </p>
            
            <h3>4.3 Credit Rollover</h3>
            <p>
              Creator, Pro, Business, and Enterprise plans include automatic credit rollover. Unused credits accumulate up to 
              3x your monthly allocation. Credits do not roll over on Free or Learner plans. Annual subscriptions receive 
              their full yearly allocation upfront without monthly rollover.
            </p>
            
            <h3>4.4 Purchased Credits</h3>
            <p>
              Additional credits can be purchased separately and never expire. Subscription credits are always used first 
              before purchased credits. Purchased credits are non-refundable except as required by law.
            </p>
            
            <h2>5. Payment Terms</h2>
            <h3>5.1 Accepted Payment Methods</h3>
            <ul>
              <li>Credit and debit cards (Visa, Mastercard, American Express) via Stripe</li>
              <li>PayPal for subscriptions and one-time purchases</li>
              <li>Cryptocurrency (Bitcoin, Ethereum, USDT, USDC, and others) via NOWPayments</li>
            </ul>
            
            <h3>5.2 Billing</h3>
            <p>
              Subscriptions are billed automatically on a monthly or annual basis. Prices are in USD unless otherwise specified. 
              We reserve the right to modify pricing with 30 days' notice to existing subscribers.
            </p>
            
            <h3>5.3 Cryptocurrency Payments</h3>
            <p>
              Cryptocurrency payments must be completed within 30 minutes of invoice generation. Cryptocurrency subscriptions 
              require manual renewal. Exchange rate fluctuations may affect the amount due. We recommend stablecoins (USDT/USDC) 
              to avoid volatility.
            </p>
            
            <h3>5.4 Refunds</h3>
            <p>
              Subscription fees and credit purchases are non-refundable except where required by law. Refund requests must be 
              submitted within 14 days of purchase to support@creolecentric.com.
            </p>
            
            <h2>6. API Usage and Developer Terms</h2>
            <h3>6.1 API Access</h3>
            <p>
              API access is available for Creator subscription plans and above, or by purchasing 85,000+ credits (the Creator Pack). You must authenticate using your API key and comply with
              rate limits based on your subscription plan or credit-based tier.
            </p>
            
            <h3>6.2 Rate Limits</h3>
            <p>
              Hourly request limits apply per authenticated account and vary by voice engine (Creole Regional vs. Creole Studio and Creole Studio Pro). The limits below apply to both web and API usage. Note that Free and Learner plans do not have API access (see section 6.1), but the limits still apply to web-based usage.
            </p>
            <ul>
              <li><strong>Free</strong> — Regional 10/hr, Studio 60/hr</li>
              <li><strong>Learner</strong> — Regional 60/hr, Studio 300/hr</li>
              <li><strong>Creator</strong> — Regional 300/hr, Studio 1,000/hr</li>
              <li><strong>Pro</strong> — Regional 1,000/hr, Studio 3,000/hr</li>
              <li><strong>Business</strong> — Regional 3,000/hr, Studio 5,000/hr</li>
              <li><strong>Enterprise</strong> — Regional custom, Studio 20,000/hr (custom)</li>
            </ul>
            
            <h3>6.3 API Restrictions</h3>
            <p>You may not:</p>
            <ul>
              <li>Share, sell, or distribute API keys</li>
              <li>Use the API to create competing services</li>
              <li>Attempt to reverse engineer our technology</li>
              <li>Exceed rate limits or attempt to circumvent restrictions</li>
              <li>Use the API for illegal or harmful purposes</li>
            </ul>
            
            <h2>7. Acceptable Use Policy</h2>
            <h3>7.1 Prohibited Uses</h3>
            <p>You agree not to use our services to:</p>
            <ul>
              <li>Generate content that is illegal, harmful, threatening, abusive, harassing, defamatory, or offensive</li>
              <li>Create deepfakes or impersonate others without consent</li>
              <li>Generate misleading or fraudulent audio content</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe upon intellectual property rights</li>
              <li>Distribute malware, spam, or engage in phishing</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Use automated systems to abuse our services</li>
            </ul>
            
            <h3>7.2 Content Moderation</h3>
            <p>
              We reserve the right to review and remove content that violates these Terms. Repeated violations may result 
              in account suspension or termination.
            </p>
            
            <h2>8. Intellectual Property Rights</h2>
            <h3>8.1 Our Intellectual Property</h3>
            <p>
              All content, features, functionality, technology, and intellectual property related to CreoleCentric services 
              are owned by CreoleCentric Labs and protected by international copyright, trademark, patent, trade secret, 
              and other intellectual property laws.
            </p>
            
            <h3>8.2 Your Content</h3>
            <p>
              You retain ownership of text content you submit to our services. By using our services, you grant us a 
              non-exclusive, worldwide, royalty-free license to process your content solely for the purpose of providing 
              our services to you.
            </p>
            
            <h3>8.3 Generated Audio</h3>
            <p>
              You own the audio files generated using our services, subject to compliance with these Terms. You are 
              responsible for ensuring your use of generated audio complies with all applicable laws and does not 
              infringe upon third-party rights.
            </p>
            
            <h2>9. Privacy and Data Protection</h2>
            <p>
              Your use of our services is also governed by our Privacy Policy, which describes how we collect, use, 
              and protect your personal information. By using our services, you consent to our data practices as 
              described in the Privacy Policy.
            </p>
            
            <h2>10. Third-Party Services</h2>
            <p>
              Our services may integrate with third-party services (payment processors, cloud storage, analytics). 
              Your use of third-party services is subject to their respective terms and privacy policies. We are not 
              responsible for third-party services or content.
            </p>
            
            <h2>11. Disclaimers and Warranties</h2>
            <h3>11.1 Service Availability</h3>
            <p>
              Our services are provided "AS IS" and "AS AVAILABLE" without warranties of any kind, either express or 
              implied. We do not guarantee uninterrupted, secure, or error-free operation of our services.
            </p>
            
            <h3>11.2 Accuracy</h3>
            <p>
              While we strive for high-quality output, we do not guarantee the accuracy, completeness, or suitability 
              of translations or generated speech for any particular purpose. Users should review and verify all output 
              before use in critical applications.
            </p>
            
            <h2>12. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, CreoleCentric and its affiliates, officers, employees, agents, 
              partners, and licensors shall not be liable for any indirect, incidental, special, consequential, or 
              punitive damages, including but not limited to loss of profits, data, use, goodwill, or other intangible 
              losses resulting from:
            </p>
            <ul>
              <li>Your use or inability to use our services</li>
              <li>Any unauthorized access to or alteration of your content</li>
              <li>Any third-party conduct or content on our services</li>
              <li>Any interruption or cessation of services</li>
            </ul>
            <p>
              In no event shall our total liability exceed the amount paid by you to CreoleCentric in the twelve (12) 
              months preceding the event giving rise to liability.
            </p>
            
            <h2>13. Indemnification</h2>
            <p>
              You agree to defend, indemnify, and hold harmless CreoleCentric and its affiliates from any claims, 
              damages, obligations, losses, liabilities, costs, or expenses (including attorney's fees) arising from:
            </p>
            <ul>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Your use or misuse of our services</li>
              <li>Content you submit, post, or transmit through our services</li>
            </ul>
            
            <h2>14. Dispute Resolution</h2>
            <h3>14.1 Informal Resolution</h3>
            <p>
              Before filing a claim, you agree to try to resolve any dispute informally by contacting us at 
              support@creolecentric.com. We will attempt to resolve the dispute informally for 30 days.
            </p>
            
            <h3>14.2 Binding Arbitration</h3>
            <p>
              If informal resolution fails, any dispute shall be resolved through binding arbitration in accordance 
              with the American Arbitration Association's Commercial Arbitration Rules. Arbitration shall be conducted 
              in English.
            </p>
            
            <h3>14.3 Class Action Waiver</h3>
            <p>
              You agree to resolve disputes only on an individual basis and waive any right to participate in class 
              actions, class arbitrations, or representative actions.
            </p>
            
            <h2>15. Modifications to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. Material changes will be notified via email or 
              prominent website notice at least 30 days before taking effect. Continued use of our services after 
              changes constitutes acceptance of the modified Terms.
            </p>
            
            <h2>16. Termination</h2>
            <p>
              You may terminate your account at any time through your account settings. We may terminate or suspend 
              your account immediately, without prior notice, if you breach these Terms. Upon termination, your right 
              to use our services will cease immediately.
            </p>
            
            <h2>17. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the State of Florida,
              United States, without regard to its conflict of law provisions. Any legal action or proceeding shall
              be brought exclusively in the state or federal courts located in St. Johns County, Florida.
            </p>
            
            <h2>18. Severability</h2>
            <p>
              If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions shall 
              continue in full force and effect. The invalid provision shall be replaced with a valid provision that 
              most closely approximates the intent of the original.
            </p>
            
            <h2>19. Entire Agreement</h2>
            <p>
              These Terms, together with our Privacy Policy and any other agreements expressly incorporated by reference, 
              constitute the entire agreement between you and CreoleCentric regarding our services and supersede all 
              prior agreements and understandings.
            </p>
            
            <h2>20. Contact Information</h2>
            <p>
              For questions about these Terms of Service, please contact us at:
            </p>
            <p>
              <strong>iTech Home Solutions, Inc.</strong> <em>(operating as CreoleCentric)</em><br />
              Email: support@creolecentric.com<br />
              Website: https://creolecentric.com<br />
              Address: 2220 CR-210 W, Suite 108-171, St Johns, FL 32259
            </p>
            
            <p className="text-sm text-gray-500 mt-8">
              Last updated: October 2, 2026<br />
              Version: 2.1
            </p>
          </div>
          </LegalPageChrome>
        </div>
      </div>
    </LegalLayout>
    </LanguageProvider>
  );
}
